import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import SelectPlan from '../select-plan';
import ModifySubscriptionProgressBar from './components/modify-subscription-progress-bar';
import UpgradePlan from '../upgrade-plan';
import type { IProps } from './modify-subscription-container';
import { BillingOption } from '../../../../../../shared/utils/billing-options';
import { ModifySubscriptionSteps, SetPlanDetails } from './types';
import RadioButtonTransfer from '../../../../../../shared/design-system/components/atoms/radio-button-transfer';
import {
  billingOptions,
  gePlanCycleBasedOnPlanType,
  getHighlightMessage,
  getDisabledLabel,
  getDisabledTab,
  isBillingAndSubscriptionPageReady,
  isColdEmailPlanBannerShow,
  isValueBasedPlanVisible,
} from './utils/helper';
import {
  ModifyPlanAction,
  PlanType,
  SubscriptionPlanModes,
  SubscriptionPlans,
} from '../../../../../../shared/utils/subscription-plans';
import ImageIcon from '../../../../../../shared/components/images/image-icon';
import { Images } from '../../../../../../shared/app-constants';
import Spinner from '../../../../../../shared/design-system/components/atoms/custom-spinner';
import { getPlanMetaData } from '../select-plan/utils/get-plan-meta-data';
import {
  getPlanId,
  getPlanNameByPlanCycle,
  getPlanNameByPlanMode,
} from '../upgrade-plan/utils/helper';
import { getPlanCode } from '../select-plan/utils/helper';
import PaymentFailed from '../upgrade-plan/payment-failed';
import { executeOnRequestStatus } from '../../../../../../shared/utils/execute-on-request-status';
import ColdEmailPlanExploreBanner from './components/cold-email-plan-explore-banner';
import OutreachPlans from '../outreach-plans';
import { showGeneralErrorNotification } from '../../../../../../shared/utils/errors';

const ModifySubscription: React.FC<IProps> = (containerProps) => {
  const { t } = useTranslation();
  const location = useLocation<{
    modifySubscriptionStep: ModifySubscriptionSteps;
    isRedirectedFromBillingOptions: boolean;
    isRedirectedFromEmailAccounts?: boolean;
  }>();

  // Select Plan
  const {
    selectPlan,
    selectedPlanDetails,
    sendGetCalculatePayRequest,
    sendGetAccountSubscriptionResponse,
    sendGetPlansResponse,
    userCurrentPlan,
    sendPurchaseSubscriptionRequestStatus,
    sendGetPurchaseModifySubscriptionRequestStatus,
    resetPurchaseModifySubscriptionResponse,
    resetCalculatePayState,
    sendGetAccountSubscriptionRequestStatus,
    sendGetPlansRequestStatus,
    subscriptionPlanStatus,
  } = containerProps;

  // States
  const [
    modifySubscriptionStep,
    setModifySubscriptionStep,
  ] = useState<ModifySubscriptionSteps>(ModifySubscriptionSteps.SelectPlan);
  const [planCycle, setPlanCycle] = useState<BillingOption>(
    gePlanCycleBasedOnPlanType(
      sendGetAccountSubscriptionResponse?.planCode,
      sendGetAccountSubscriptionResponse?.planMode,
    ),
  );
  const [isPaymentFailed, setIsPaymentFailed] = useState(false);

  const setPlanDetails = ({
    planCode,
    planType,
    planMode,
    planName,
    action,
  }: SetPlanDetails) => {
    const {
      planName: currentPlanName,
      planMode: currentPlanMode,
      planAmount,
      planId,
    } = sendGetAccountSubscriptionResponse || {};

    const getPlanName = getPlanNameByPlanMode(
      sendGetPlansResponse,
      planCode,
      planMode,
    );

    let modifyAction = action;

    if (
      currentPlanMode === SubscriptionPlanModes.MonthlyMode &&
      planCycle === BillingOption.ANNUALLY
    ) {
      modifyAction = ModifyPlanAction.Update;
    } else if (currentPlanName === planName) {
      modifyAction = ModifyPlanAction.AddSlots;
      resetCalculatePayState();
    }

    const { displayName, price } =
      getPlanMetaData(planCode, planCycle, planName || getPlanName) || {};

    selectPlan({
      displayName: displayName || planName,
      planId:
        getPlanId(
          sendGetPlansResponse,
          planName || getPlanName,
          planCode,
          planType,
        ) || planId,
      planPrice: price || Number(planAmount),
      planCode,
      planType,
      action: modifyAction,
    });
  };

  const handleModifySubscriptionStep = (step) =>
    setModifySubscriptionStep(step);

  const handelBillingOption = (value) => setPlanCycle(value);

  const handelSelectPlan = (
    planCode: SubscriptionPlans,
    planType: PlanType,
    planName,
    planMode: number,
    action,
  ) => {
    const planId = getPlanId(
      sendGetPlansResponse,
      planName,
      planCode,
      planType,
    );

    setPlanDetails({ planCode, planType, planMode, planName, action });
    handleModifySubscriptionStep(ModifySubscriptionSteps.SelectSlots);

    if (action === ModifyPlanAction.AddSlots) {
      resetCalculatePayState();
    }

    if (
      action !== ModifyPlanAction.AddSlots &&
      planCycle !== BillingOption.LIFETIME &&
      userCurrentPlan !== SubscriptionPlans.FREE &&
      userCurrentPlan !== SubscriptionPlans.TRIAL
    ) {
      sendGetCalculatePayRequest({
        accountSubscriptionPlanId: planId,
        slots: sendGetAccountSubscriptionResponse?.slots,
      });
    }
  };

  const onPaymentFailed = () => {
    setIsPaymentFailed(true);
  };

  const onPaymentRetrieve = () => {
    setIsPaymentFailed(false);
    setModifySubscriptionStep(ModifySubscriptionSteps.SelectPlan);
  };

  useEffect(() => {
    const { modifySubscriptionStep: step } = location.state || {
      modifySubscriptionStep: null,
    };

    if (step) {
      resetCalculatePayState();
      setModifySubscriptionStep(step);
    }
  }, []);

  useEffect(() => {
    executeOnRequestStatus({
      status: sendGetAccountSubscriptionRequestStatus,
      onPending: () => {
        setIsPaymentFailed(false);
      },
      onSuccess: () => {
        if (sendGetAccountSubscriptionResponse) {
          const { planCode, planName } = sendGetAccountSubscriptionResponse;
          setPlanCycle(
            gePlanCycleBasedOnPlanType(
              sendGetAccountSubscriptionResponse?.planCode,
              sendGetAccountSubscriptionResponse?.planMode,
            ),
          );
          setPlanDetails({
            planCode,
            planName,
            action: ModifyPlanAction.AddSlots,
          });
        }
      },
    });
  }, [sendGetAccountSubscriptionRequestStatus, sendGetPlansRequestStatus]);

  useEffect(() => {
    if (selectedPlanDetails && sendGetAccountSubscriptionResponse) {
      const { planName, planCode } = sendGetAccountSubscriptionResponse;
      setPlanDetails({
        planCode:
          planCode === SubscriptionPlans.PAID_MONTHLY ||
          planCode === SubscriptionPlans.PAID_YEARLY
            ? getPlanCode(planCycle)
            : planCode,
        planName: getPlanNameByPlanCycle(
          selectedPlanDetails?.displayName || planName,
          planCycle,
        ),
        action: selectedPlanDetails.action,
      });
    }
  }, [planCycle]);

  useEffect(() => {
    executeOnRequestStatus({
      status: sendPurchaseSubscriptionRequestStatus,
      onFailed: () => {
        containerProps.hideLoading();
        onPaymentFailed();
      },
    });
  }, [sendPurchaseSubscriptionRequestStatus]);

  useEffect(() => {
    executeOnRequestStatus({
      status: sendGetPurchaseModifySubscriptionRequestStatus,
      onFailed: () => {
        containerProps.hideLoading();
        onPaymentFailed();
        resetPurchaseModifySubscriptionResponse();
      },
    });
  }, [sendGetPurchaseModifySubscriptionRequestStatus]);

  useEffect(() => {
    const {
      sendGetAccountSubscriptionRequest,
      sendGetPlansRequest,
      sendGetConnectedUsersAndEmailAccountsRequest,
    } = containerProps;
    sendGetAccountSubscriptionRequest();
    sendGetPlansRequest();
    sendGetConnectedUsersAndEmailAccountsRequest();
  }, []);

  const {
    sendGetConnectedUsersAndEmailAccountsRequestError,
    sendGetConnectedUsersAndEmailAccountsRequestStatus,
    resetConnectedUsersAndEmailAccountsRequest,
  } = containerProps;

  useEffect(() => {
    executeOnRequestStatus({
      status: sendGetConnectedUsersAndEmailAccountsRequestStatus,
      onSuccess: () => {
        resetConnectedUsersAndEmailAccountsRequest();
      },
      onFailed: () => {
        showGeneralErrorNotification(
          sendGetConnectedUsersAndEmailAccountsRequestError?.message,
        );
        resetConnectedUsersAndEmailAccountsRequest();
      },
    });
  }, [sendGetConnectedUsersAndEmailAccountsRequestStatus]);

  // Check it payment failed or not
  if (isPaymentFailed) {
    return <PaymentFailed onRetryPayment={() => onPaymentRetrieve()} />;
  }

  const isRequestPending = isBillingAndSubscriptionPageReady({
    sendGetAccountSubscriptionRequestStatus,
    sendGetPlansRequestStatus,
    sendGetAccountSubscriptionResponse,
    selectedPlanDetails,
  });

  const subscriptionStep = () =>
    modifySubscriptionStep === ModifySubscriptionSteps.SelectPlan ? (
      <div className="d-flex justify-content-center mt-4">
        <SelectPlan
          planCycle={planCycle}
          onSelectPlan={(planCode, planType, planName, planMode, action) =>
            handelSelectPlan(planCode, planType, planName, planMode, action)
          }
          sendGetAccountSubscriptionResponse={
            sendGetAccountSubscriptionResponse
          }
          sendGetPlansResponse={sendGetPlansResponse}
        />
      </div>
    ) : (
      <UpgradePlan
        containerProps={containerProps}
        planCycle={planCycle}
        isRedirectedFromEmailAccounts={
          location.state?.isRedirectedFromEmailAccounts
        }
      />
    );

  if (isRequestPending) {
    return <Spinner />;
  }

  if (
    isValueBasedPlanVisible(
      sendGetAccountSubscriptionResponse?.planCode,
      sendGetAccountSubscriptionResponse?.planType,
      subscriptionPlanStatus,
    )
  ) {
    return <OutreachPlans />;
  }

  return (
    <>
      <div className="d-flex flex-column align-items-center">
        {isColdEmailPlanBannerShow(
          sendGetAccountSubscriptionResponse?.planCode,
        ) && <ColdEmailPlanExploreBanner />}
        <ModifySubscriptionProgressBar
          modifySubscriptionStep={modifySubscriptionStep}
          onStepChange={(step) => handleModifySubscriptionStep(step)}
          disabledStep={getDisabledTab(
            sendGetAccountSubscriptionResponse?.planCode,
            sendGetAccountSubscriptionResponse?.planType,
          )}
        />
        {planCycle !== BillingOption.LIFETIME && (
          <div className="d-flex flex-column align-items-center mt-4">
            <div className="modify-subscription__highlight-container d-flex justify-content-center align-items-center position-relative font-italic">
              <span className="semibold-1 font-8 orange-txt-16">
                {t(`messages.${getHighlightMessage(planCycle)}`)}
              </span>
              <div className="modify-subscription__circle">
                <ImageIcon src={Images.HalfCircle} />
              </div>
            </div>
            <RadioButtonTransfer
              options={billingOptions}
              onChange={handelBillingOption}
              value={planCycle}
              disabledLabel={getDisabledLabel(
                sendGetAccountSubscriptionResponse,
              )}
            />
          </div>
        )}
      </div>

      {subscriptionStep()}
    </>
  );
};

export default ModifySubscription;
